import { useState, useMemo } from "react";
import { ChevronDown, Flame, SlidersHorizontal } from "lucide-react";
import clsx from "clsx";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { generateDateTicks } from "../../../services/dataTicks";

const fuelTypes = {
  "Heavy Fuel Oil": {
    name: "Heavy Fuel Oil",
    color: "#1a5276",
  },
  "Light Fuel Oil": {
    name: "Light Fuel Oil",
    color: "#27ae60",
  },
  "Marine Gas Oil": {
    name: "Marine Gas Oil",
    color: "#d35400",
  },
  "Others": {
    name: "Others",
    color: "#7d3c98",
  },
};

const FuelConsumptionChart = ({ consumptionHistory = [] }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState([
    "Heavy Fuel Oil", "Light Fuel Oil", "Marine Gas Oil", "Others"
  ]);

  const handleToggle = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const filteredHistory = useMemo(() => {
    const uniqueDates = [
      ...new Set(
        consumptionHistory.map(
          (r) => new Date(r.recordedAt).toISOString().split("T")[0]
        )
      ),
    ];

    uniqueDates.sort((a, b) => new Date(a) - new Date(b));

    return uniqueDates.map((dateStr) => {
      const record = consumptionHistory.find(
        (r) => new Date(r.recordedAt).toISOString().split("T")[0] === dateStr
      );

      return {
        recordedAt: dateStr,
        "Heavy Fuel Oil": record?.["Heavy Fuel Oil"] ?? 0,
        "Light Fuel Oil": record?.["Light Fuel Oil"] ?? 0,
        "Marine Gas Oil": record?.["Marine Gas Oil"] ?? 0,
        Others: record?.["Others"] ?? 0,
      };
    });
  }, [consumptionHistory]);

  // Prepare series data for Highcharts - filtered by selected types
  const categories = filteredHistory.map((entry) =>
    new Date(entry.recordedAt).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    })
  );
  const dates = filteredHistory.map(item => item.recordedAt);
  let tickPositions = generateDateTicks(dates);

  // Filter fuel data based on selected types
  const fuelData = {
    "Heavy Fuel Oil": selectedTypes.includes("Heavy Fuel Oil") ? filteredHistory.map((entry) => entry["Heavy Fuel Oil"] || 0) : [],
    "Light Fuel Oil": selectedTypes.includes("Light Fuel Oil") ? filteredHistory.map((entry) => entry["Light Fuel Oil"] || 0) : [],
    "Marine Gas Oil": selectedTypes.includes("Marine Gas Oil") ? filteredHistory.map((entry) => entry["Marine Gas Oil"] || 0) : [],
    "Others": selectedTypes.includes("Others") ? filteredHistory.map((entry) => entry["Others"] || 0) : [],
  };

  const options = {
    chart: {
      type: "scatter",
      zoomType: "xy",
      backgroundColor: "transparent",
      height: isOpen ? 420 : 0,
    },
    title: {
      text: null,
    },
    xAxis: {
      type: "datetime",
      dates,
      title: { text: null },
      gridLineWidth: 1,
      gridLineColor: "#eee",
      tickPositions: tickPositions,
      labels: {
        format: "{value:%b %d}",
        style: {
          color: "#6b7280",
        },
      },
    },
    yAxis: {
      title: { text: "Consumption (MT / Day)" },
      gridLineColor: "#eee",
      minPadding: 0,
      maxPadding: 0.05,
      tickAmount: 6,
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        // Get all series that have data at this x position (date)
        const points = this.series.chart.series.map(series => {
          const point = series.points.find(p => p.x === this.x);
          return point ? {
            color: series.color,
            name: series.name,
            y: point.y,
            date: point.date
          } : null;
        }).filter(point => point !== null);

        // Generate HTML for all points at this x position
        const pointsHTML = points.map(point => {
          return `
        <div style="display: flex; justify-content: space-between; align-items: center; padding: 2px 0;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <div style="width: 8px; height: 8px; border-radius: 50%; background-color: ${point.color};"></div>
            <span style="font-size: 12px;">${point.name}:</span>
          </div>
          <span style="font-weight: 600; font-size: 12px;">${point.y} MT</span>
        </div>
      `;
        }).join('');

        return `
      <div style="padding: 12px; min-width: 220px;">
        <div style="font-weight: 600; margin-bottom: 8px; color: #374151; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">
          ${new Date(this.x).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        })}
        </div>
        <div style="max-height: 200px; overflow-y: auto;">
          ${pointsHTML}
        </div>
      </div>
    `;
      },
      backgroundColor: "rgba(255, 255, 255, 0.98)",
      borderColor: "#e2e8f0",
      borderRadius: 8,
      shadow: true,
      style: {
        fontSize: "12px"
      }
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 6,
          symbol: "circle",
          states: {
            hover: { enabled: true, lineColor: "#333" },
          },
        },
      },
    },
    series: Object.entries(fuelData)
      .filter(([name, data]) => data.length > 0)
      .map(([name, values], i) => ({
        name,
        data: values.map((value, index) => ({
          x: new Date(dates[index]).getTime(),
          y: value,
          date: new Date(dates[index]).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
          }),
        })),
        color: fuelTypes[name].color,
      })),
    credits: { enabled: false },
  };

  return (
    <div className="w-full rounded-md bg-white shadow-xl backdrop-blur-md overflow-hidden transition-all duration-300">
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-gradient-to-br from-[#1a325d] to-[#12213f] w-full flex items-center justify-between px-4 py-3 text-left focus:outline-none group"
      >
        <div className="flex items-center gap-3">
          <div className="text-blue-400 hover:ring-blue-400 transition-all duration-300">
            <Flame size={20} strokeWidth={2} />
          </div>
          <h3 className="text-[15px] font-semibold text-white tracking-wide">
            ME Fuel Consumption
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <ChevronDown
            className={clsx(
              "w-5 h-5 text-white transition-transform duration-300",
              isOpen && "rotate-180"
            )}
          />
        </div>
      </button>

      {/* Filter Section */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-blue-600" />
            Select by:
          </label>
          <div className="flex flex-wrap gap-2">
            {Object.keys(fuelTypes).map((type) => {
              const { name, color } = fuelTypes[type];
              const isActive = selectedTypes.includes(type);

              return (
                <button
                  key={type}
                  onClick={() => handleToggle(type)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 border flex items-center gap-2
                    ${isActive
                      ? "text-white shadow-sm border-transparent"
                      : "text-gray-600 bg-white border-gray-300 hover:bg-gray-50"
                    }`}
                  style={{
                    backgroundColor: isActive ? color : undefined,
                  }}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div
        className={clsx(
          "transition-all duration-500 overflow-hidden px-4",
          isOpen ? "py-4 max-h-[500px]" : "py-0 max-h-0"
        )}
      >
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  );
};

export default FuelConsumptionChart;